/* eslint-disable no-underscore-dangle */
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { selectContext } from '@icp/form-renderer-core';
import { Loading } from '@icp/components';
import { getSearchParam, loadDHTMLXGantt, randomNumber, updateSearchParam } from '@icp/utils';
import { Modal } from 'antd';
import { isEqual } from 'lodash-es';
import { useEventCallback } from '@icp/hooks';
import { useParams } from 'react-router-dom';
import { useClassName, useConditionalProperty, useVariablePattern } from '../../hooks';
import { DataFiltersType, SingleDataSourceTypes } from '../../propTypes';
import { useElementDecorator, useFormApi, useIsInDesign } from '../../FormRenderCtx';
import { withFieldWrapper } from '../../fieldWrapper';
import { useStore } from '../../store';
import GanttToolbar from './GanttToolbar';
import ButtonElement from '../ButtonElement';
import { CurrentDataProvider, useCurrentData } from '../../currentDataCtx';
import useGanttDataSource from './useGanttDataSource';
import RecursionRenderer from '../../RecursionRenderer';
import {
  addTodayMarker,
  formatGanttColumns,
  loadGanttDefaultState,
  loadGanttInitState,
  setAllGanttColumnState,
} from './utils';
import setGanttConfig from './setGanttConfig';
import setGanttCalendar from './setGanttCalendar';
import { makeSettingKey } from '../TableElement/utils';
import { saveSetting } from './settingHandler';

const EMPTY_OBJECT = {};
const DEFAULT_DATE_PROPS = {
  showTime: false,
  endDateIncludeSelf: true,
};

const GanttElement = forwardRef(function GanttElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    style,
    componentProps = {},
    readonly: readonlyProp,
    //
  } = props;
  const {
    style: componentStyle,
    // 为了统一配置方便 columnDefs 使用和 AG-Grid 一样的配置
    columnDefs: columnDefsProp,
    dataSources = EMPTY_OBJECT,
    relation: relationProp = EMPTY_OBJECT,
    mapping = EMPTY_OBJECT,
    ganttConfig = EMPTY_OBJECT,
    dateOptions: dateOptionsProp,
    toolbarFields,
    toolbarChildren: toolbarChildrenProp,
    pinnedFilter,
    settingKey: settingKeyProp,
    defaultFilterModel,
    suppressSaveSetting,
    suppressAddButton = false,
    addButtonProps = EMPTY_OBJECT,
  } = componentProps;
  const dateOptions = useMemo(() => {
    return { ...DEFAULT_DATE_PROPS, ...dateOptionsProp };
  }, [dateOptionsProp]);

  const { t } = useTranslation(['icp-form-renderer', 'icp-vendor-dhtmlx-gantt']);

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();
  const params = useParams();
  const store = useStore();
  const formApi = useFormApi();
  const context = selectContext(store.getState());
  const currentData = useCurrentData();
  const readonly = useConditionalProperty(readonlyProp);

  const [modal, contextHolder] = Modal.useModal();

  const settingKey = useMemo(() => {
    if (isInDesign || suppressSaveSetting) return null;
    return settingKeyProp || makeSettingKey(id, context, 'gantt');
  }, [context, id, isInDesign, settingKeyProp, suppressSaveSetting]);
  const searchKey = id ? `${id}-search` : null;

  const defaultGanttState = useMemo(() => {
    // 其余属性会自动填充成 undefined，给 Toolbar reset state 以及切换成默认视图的时候覆盖已有设置使用
    return loadGanttDefaultState({
      zoomLevel: 'month',
      filter: {
        filterModel: defaultFilterModel,
      },
    });
  }, [defaultFilterModel]);

  const [ganttLoaded, setGanttLoaded] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState({});
  const [loading, setLoading] = useState(false);
  const [ganttState, setGanttState] = useState(() => {
    return loadGanttInitState(settingKey, defaultGanttState);
  });
  const [searchText, setSearchText] = useState(getSearchParam(searchKey) || '');

  const { filter, currentCalendar } = ganttState;
  const { filterModel } = filter;

  const isUnMount = useRef(null);
  const nodeRef = useRef(null);
  const eleRef = useRef(null);
  const ganttRef = useRef(null);
  const refreshTodayMarker = useRef(null);
  const readyToSaveSetting = useRef(false);
  const ganttStateRef = useRef(null);
  ganttStateRef.current = ganttState;
  const currentDataRef = useRef(null);
  currentDataRef.current = currentData;

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);

  const [dialogTask, setDialogTask] = useState(null);
  const dialogBtnRef = useRef(null);
  const ganttApiRef = useRef(null); // 给 GanttActionCellRenderer 用
  const deletePromisesRef = useRef([]); // 给 GanttActionCellRenderer 里处理 loading 和刷新使用

  // relation 来实现类似 EditableTable 一样的绑定 Gantt 所有数据到某一个父表单数据
  const relationValue = useVariablePattern(relationProp?.value, true);
  const relation = useMemo(() => {
    return {
      colId: relationProp.colId,
      value: relationValue,
    };
  }, [relationProp.colId, relationValue]);

  const isTreeDataSource = !!dataSources.task?.child;

  const columnDefs = useMemo(() => {
    return formatGanttColumns({
      store,
      params,
      columnDefsProp,
      ganttApiRef,
      deletePromisesRef,
      mapping,
      readonly,
      isTreeDataSource,
      dateOptions,
      dataSource: dataSources.task?.dataSource,
      currentDataRef,
    });
  }, [
    store,
    params,
    columnDefsProp,
    mapping,
    readonly,
    isTreeDataSource,
    dateOptions,
    dataSources.task?.dataSource,
  ]);

  const {
    loading: dataLoading,
    error,
    ganttData,
    calendarData,
  } = useGanttDataSource({
    dataSources,
    refresh: refreshFlag,
    mapping,
    dateOptions,
    relation,
    filterModel,
    searchText,
    columnDefs,
  });

  useEffect(() => {
    return () => {
      isUnMount.current = true;
    };
  }, []);

  useEffect(() => {
    setLoading(false);
  }, [dataLoading, error, ganttData]);

  // 不响应 id 的变化
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idToRegister = useMemo(() => id || `random-gantt-id-${randomNumber(100000)}`, []);
  useEffect(() => {
    formApi.asyncComponentManager.register(idToRegister);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = useCallback((task) => {
    setDialogTask(task);
    setTimeout(() => {
      dialogBtnRef.current.node.click();
    }, 16);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshFlag({});
  }, []);

  const ganttApi = useMemo(() => {
    return {
      node: nodeRef.current,
      get gantt() {
        return ganttRef.current;
      },
      refresh: handleRefresh,
      openDialog,
      setLoading,
    };
  }, [handleRefresh, openDialog]);
  ganttApiRef.current = ganttApi;

  useImperativeHandle(ref, () => ganttApi, [ganttApi]);

  const handleGanttStateChange = useEventCallback((obj, notSave) => {
    if (!readyToSaveSetting.current || isUnMount.current) {
      return;
    }

    if (!(obj && typeof obj === 'object')) {
      return;
    }

    const newState = { ...ganttState, ...obj };

    if (isEqual(newState, ganttState)) {
      return;
    }

    if (Object.keys(obj).includes('newZoomLevel')) {
      refreshTodayMarker.current?.();
    }

    setGanttState(newState);
    // 只存修改过的状态
    if (!notSave) {
      saveSetting(settingKey, obj);
    }

    if (Object.keys(obj).length > 1) {
      // 同时修改多个属性表示 switch favorite view, 需要调用 gantt 的 api 去一个个应用上去
      setAllGanttColumnState({ gantt: ganttRef.current, columnDefs, ganttState: obj });
    }
  });

  useEffect(() => {
    let gantt;

    loadDHTMLXGantt().then((res) => {
      gantt = res.Gantt.getGanttInstance();

      setGanttConfig({
        isInDesign,
        gantt,
        columnDefs,
        openDialog,
        modal,
        ganttConfig,
        dataSources,
        mapping,
        relation,
        deletePromisesRef,
        context,
        readonly,
        ganttStateRef,
        onGanttStateChange: handleGanttStateChange,
        isUnMount,
        isTreeDataSource,
        dateOptions,
      });

      if (eleRef.current) {
        gantt.init(eleRef.current);
        ganttRef.current = gantt;
        setGanttLoaded(true); // 刷新 toolbar 以及加载数据 effect
        formApi.asyncComponentManager.setReady(idToRegister);
      }
    });

    return () => {
      ganttRef.current = null;
      gantt?.destructor();
    };
  }, [
    isInDesign,
    columnDefs,
    context,
    dataSources,
    formApi,
    ganttConfig,
    idToRegister,
    mapping,
    modal,
    openDialog,
    relation,
    readonly,
    handleGanttStateChange,
    isTreeDataSource,
    dateOptions,
  ]);

  useEffect(() => {
    if (ganttData && ganttRef.current) {
      ganttRef.current.clearAll();
      // clearAll 会同时删除 marker，所以不能在 setGanttConfig 里初始化 marker，等每次数据变化过后重新添加
      if (ganttData.data?.length) {
        refreshTodayMarker.current = addTodayMarker(ganttRef.current);
      }
      ganttRef.current.parse(ganttData);

      // 防止初始化设置 data 触发的 scroll 事件修改 scrollLeft 等。
      setTimeout(() => {
        readyToSaveSetting.current = true;
      }, 1000);
    }
  }, [ganttData, ganttLoaded]);

  useEffect(() => {
    if (Array.isArray(calendarData) && ganttRef.current) {
      const calendarConfig = currentCalendar
        ? calendarData.find((item) => item.id === currentCalendar)
        : calendarData[0];
      if (calendarConfig) {
        setGanttState((oldState) => ({
          ...oldState,
          currentCalendar: calendarConfig.id,
        }));
        setGanttCalendar({ gantt: ganttRef.current, calendarConfig });
      }
    }
  }, [ganttLoaded, currentCalendar, calendarData]);

  const handleFuzzySearch = (val) => {
    setSearchText(val);
    updateSearchParam(searchKey, val);
  };

  if (relation.colId && !relationValue) {
    throw new Error(
      `${t('gantt.invalid-relation', { valueConfig: relationProp.value })}. ${JSON.stringify(relationProp)}`,
    );
  }

  const toolbarAddButton =
    !readonly && !suppressAddButton && dataSources.task?.dataSource ? (
      <ButtonElement
        {...addButtonProps}
        componentProps={{
          content: t('add', { ns: 'icp-common' }),
          icon: 'ant:plus-circle-outlined',
          type: 'primary',
          ...addButtonProps.componentProps,
          action: undefined,
          className: clsx('icp-toolbar-add-button', addButtonProps.componentProps?.className),
          onClick: () => {
            openDialog({
              _original: {},
              _dataSourceConfig: dataSources.task,
            });
          },
        }}
      />
    ) : null;
  // GanttActionCellRenderer 里无法调用 ButtonElement，同时双击编辑也需要打开弹窗。所以这里固定一个只能执行 openDialog 的按钮，使用 openDialog 方法来打开
  const openDialogButton = (() => {
    if (isInDesign || readonly || !dialogTask?._dataSourceConfig) {
      return null;
    }

    // const dataSourceConfig = dialogTask._dataSourceConfig;
    let dataSourceConfig = dialogTask._dataSourceConfig;
    let dialogTitle;
    let buttonProps;
    if (dialogTask.id) {
      // edit
      dialogTitle = `${t('edit', { ns: 'icp-common' })}: :${dataSourceConfig.mapping.text}`;
      buttonProps = dialogTask._dataSourceConfig.editButtonProps;
    } else if (dialogTask._isAddSubTask) {
      // add sub task
      dataSourceConfig = isTreeDataSource ? dataSourceConfig.child : dataSourceConfig;
      dialogTitle = t('gantt.new-sub-task', { ns: 'icp-form-renderer' });
      buttonProps = dialogTask._dataSourceConfig.addButtonProps;
    } else {
      // toolbar add
      dialogTitle = t('gantt.new-task', { ns: 'icp-form-renderer' });
      buttonProps = addButtonProps;
    }

    // 这里只是为了配置打开弹窗的行为，Button 本身的显示在 Toolbar 和 GanttActionCellRenderer 中，所以这里只
    // 允许覆盖 openFormProps 和 openDialogProps
    return (
      <ButtonElement
        componentProps={{
          action: {
            type: 'dialog',
            successAction: {
              type: 'refreshData',
              fieldIds: [id],
            },
            openFormProps: buttonProps?.componentProps?.action?.openFormProps
              ? {
                  ...buttonProps?.componentProps?.action?.openFormProps,
                  // 暂时不允许覆盖默认的 defaultData，否则新建 sub task 的时候自带 parent 和 startDate endDate 过去不好做
                  FormRendererProps: {
                    defaultData: ':currentData',
                  },
                }
              : {
                  formType: 'ENTITY',
                  pbcToken: dataSourceConfig.dataSource.pbcToken,
                  formEntityToken: dataSourceConfig.dataSource.token,
                  formEntityLayoutToken: dataSourceConfig.dataSource.layoutToken,
                  formEntityDataId: dialogTask.id,
                  FormRendererProps: {
                    defaultData: ':currentData',
                  },
                },
            openDialogProps: buttonProps?.componentProps?.action?.openDialogProps || {
              size: 'md',
              disableUseOpenContentTitle: true,
              title: dialogTitle,
            },
          },
        }}
        style={{ display: 'none' }}
        ref={dialogBtnRef}
      />
    );
  })();

  const toolbarNoChildren = !toolbarChildrenProp && !toolbarFields?.length;
  const toolbarChildren =
    isInDesign && toolbarNoChildren ? (
      <div
        key="empty"
        data-key-path={(keyPath || []).concat('componentProps', 'toolbarFields')}
        className="form-element toolbar-fields-empty no-children"
      />
    ) : (
      toolbarChildrenProp || (
        <RecursionRenderer
          keyPath={(keyPath || []).concat('componentProps', 'toolbarFields')}
          fields={toolbarFields}
        />
      )
    );

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx('gantt-element form-element', className, classNameComp, {
          'in-design': isInDesign,
        })}
        style={{ ...style, ...componentStyle }}
        ref={nodeRef}
      >
        <GanttToolbar
          isInDesign={isInDesign}
          gantt={ganttRef.current}
          ganttConfig={ganttConfig}
          columnDefs={columnDefs}
          pinnedFilter={pinnedFilter}
          defaultGanttState={defaultGanttState}
          ganttState={ganttState}
          searchText={searchText}
          onRefresh={handleRefresh}
          onGanttStateChange={handleGanttStateChange}
          onSearch={handleFuzzySearch}
          suppressExpandAll={!mapping.parent}
          calendarData={calendarData}
          settingKey={settingKey}
          showTime={dateOptions.showTime}
        >
          {toolbarChildren}
          {toolbarAddButton}
        </GanttToolbar>
        <div style={{ position: 'relative', height: '100%', flex: 1, minHeight: 0 }}>
          <div style={{ height: '100%' }} ref={eleRef} />
          {loading || dataLoading ? <Loading delayed={false} overlay={true} /> : null}
          {error ? (
            <div className="icp-loading-overlay icp-center" style={{ color: 'var(--error-color)' }}>
              {error.message ?? `${error.error}`}
            </div>
          ) : null}
        </div>
        <CurrentDataProvider
          value={{
            ...dialogTask?._original,
            [relation.colId]: relation.value,
          }}
        >
          {openDialogButton}
        </CurrentDataProvider>
        {contextHolder}
      </div>
    </ElementDecorator>
  );
});

GanttElement.propTypes = {
  keyPath: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  readonly: PropTypes.bool,
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
    /**
     * 表格的列的定义，延用 ag-grid 的 columnDefs 设置
     */
    columnDefs: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        // all other ag-grid column def properties
      }),
    ),
    /**
     * 数据源
     */
    dataSources: PropTypes.shape({
      task: PropTypes.shape({
        ...SingleDataSourceTypes,
        child: PropTypes.shape({}),
        // eslint-disable-next-line react/forbid-prop-types
        parentFieldValue: PropTypes.any,
        addButtonProps: PropTypes.shape({}),
        editButtonProps: PropTypes.shape({}),
        deleteButtonProps: PropTypes.shape({}),
      }),
      link: PropTypes.shape(SingleDataSourceTypes),
      calendar: PropTypes.shape(SingleDataSourceTypes),
    }),
    /**
     * Gantt 数据作为表单子表的关系配置
     */
    relation: PropTypes.shape({
      colId: PropTypes.string,
      value: PropTypes.string,
    }),
    /**
     * 数据映射
     */
    mapping: PropTypes.shape({
      text: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      duration: PropTypes.string,
      progress: PropTypes.string,
      parent: PropTypes.string,
      type: PropTypes.string,
    }),
    /**
     * DHTMLX Gantt 组件的配置
     * https://docs.dhtmlx.com/gantt/api__refs__gantt_props.html
     */
    ganttConfig: PropTypes.shape({}),
    /**
     * 日期配置
     */
    dateOptions: PropTypes.shape({
      /**
       * 同 DatePicker 的 `noTimeZone` 属性, 拖拽 gantt bar 触发 change 的时候是否去除日期的 time zone 信息
       * @default false
       */
      noTimeZone: PropTypes.bool,
      /**
       * 同 DatePicker 的 `timeZone` 属性, 日历的时区，拖拽 gantt bar 触发 change 的时候有效
       */
      timeZone: PropTypes.string,
      /**
       * 同 DatePicker 的 `showTime` 属性， 拖拽 gantt bar 触发 change 的时候有效。同时会控制 toolbar 的 gantt
       * scale 选择能否缩放到显示精确到 天 以下。
       * @default false
       */
      showTime: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
      /**
       * 当 showTime 是 `false` 的时候，gantt 图显示的 endDate 是否包含当天。
       * @default true
       */
      endDateIncludeSelf: PropTypes.bool,
    }),
    /**
     * 使用 json 配置的工具栏的自定义元素
     */
    toolbarFields: PropTypes.arrayOf(PropTypes.shape({})),
    /**
     * 使用 jsx 的工具栏自定义元素
     */
    toolbarChildren: PropTypes.node,
    /**
     * 默认显示在 Filter Panel 的 filter 列，无法被删除
     */
    pinnedFilter: PropTypes.arrayOf(PropTypes.string),
    /**
     * 保存设置的 key，默认使用 id 自动生成
     */
    settingKey: PropTypes.string,
    /**
     * 表格默认的 filter 条件，可以通过界面进行改变
     */
    defaultFilterModel: DataFiltersType,
    /**
     * 不保存用户调整的组件设置
     * @default false
     */
    suppressSaveSetting: PropTypes.bool,
    /**
     * 禁止默认 "新增" 按钮
     * @default false
     */
    suppressAddButton: PropTypes.bool,
    /**
     * 默认新增按钮的属性
     */
    addButtonProps: PropTypes.shape({}),
  }),
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
GanttElement.displayName = 'Gantt';

export default withFieldWrapper(GanttElement, { ns: 'icp-vendor-dhtmlx-gantt' });
